// client/src/components/MerchantGestionPromo.jsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { firestore, storage } from '../../firebase';
import {
  doc, collection, query, where, onSnapshot, limit, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  ref, getDownloadURL, listAll, getMetadata, uploadBytes, deleteObject
} from 'firebase/storage';
import '../CSS/Dashboard.css';
import { useNavigate } from 'react-router-dom';
import GestionTabs from './GestionTabs';
import MerchantNavbar from './MerchantNavbar';
import { sanitize } from '../../utils/sanitize';

// =====================
// Config & petites utils
// =====================
const MAX_SIZE = {
  image: 5 * 1024 * 1024,    // 5 Mo max pour PNG/JPG
  pdf:   10 * 1024 * 1024,   // 10 Mo max pour PDF
  video: 50 * 1024 * 1024,   // 50 Mo max pour MP4
};

const ALLOWED_MIME = [
  'image/png',
  'image/jpeg',
  'application/pdf',
  'video/mp4',
];

const ACCEPT_INPUT = '.png,.jpg,.jpeg,.pdf,.mp4';

// TTL du cache (en ms) pour éviter des listages Storage trop fréquents
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const formatDateTimeLocal = (date) => {
  const pad = (n) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const sizes = ['octets', 'Ko', 'Mo', 'Go'];
  if (bytes === 0) return '0 octet';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(val >= 10 || i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const detectKind = (contentType = '', name = '') => {
  const lower = (name || '').toLowerCase();
  if (contentType.startsWith('image/') || lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image';
  if (contentType === 'application/pdf' || lower.endsWith('.pdf')) return 'pdf';
  if (contentType.startsWith('video/') || lower.endsWith('.mp4')) return 'video';
  return 'other';
};

const kindMaxSize = (kind) => {
  if (kind === 'image') return MAX_SIZE.image;
  if (kind === 'pdf') return MAX_SIZE.pdf;
  if (kind === 'video') return MAX_SIZE.video;
  return MAX_SIZE.image; // fallback prudent
};

// Helpers cache
const cacheGet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return parsed.data;
  } catch { return null; }
};
const cacheSet = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
};

const cacheDel = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

const MerchantGestionPromo = () => {
  const [user, setUser] = useState(null);
  const [showStripeModal, setShowStripeModal] = useState(false);

  const [documentFile, setDocumentFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [startDateTime, setStartDateTime] = useState("");
  const [endDateTime, setEndDateTime] = useState("");
  const [hasEndDate, setHasEndDate] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false); // UI : modale « Ajouter une promotion »

  const [pubs, setPubs] = useState([]);
  const [deleting, setDeleting] = useState(null); // nom du fichier en cours de suppression
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");

  // eslint-disable-next-line
  const [entrepriseId, setEntrepriseId] = useState(null);

  const navigate = useNavigate();
  const auth = getAuth();
  const fileInputRef = useRef(null);

  // =================
  // Auth + user doc
  // =================
  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) {
        if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }
        setUser(null);
        setEntrepriseId(null);
        setShowStripeModal(false);
        navigate('/logingeneral');
        return;
      }
      const userId = currentUser.uid;
      if (!userId) return;

      const userDocRef = doc(firestore, 'users', userId);
      if (unsubscribeUser) { unsubscribeUser(); unsubscribeUser = null; }
      unsubscribeUser = onSnapshot(userDocRef, (docSnapshot) => {
        const userData = docSnapshot.data();
        setUser({ ...userData, uid: userId, pseudo: userData?.pseudo });
      });
    });

    return () => {
      if (unsubscribeUser) unsubscribeUser();
      unsubscribeAuth();
    };
  }, [navigate, auth]);

  // =================
  // Stripe guard (1 seule souscription, limit(1))
  // =================
  useEffect(() => {
    if (!user?.uid) return;
    const entrepriseRef = collection(firestore, 'entreprises');
    const qStripe = query(entrepriseRef, where('userId', '==', user.uid), limit(1));
    const unsub = onSnapshot(qStripe, (snap) => {
      if (snap.empty) {
        setEntrepriseId(null);
        setShowStripeModal(true);
        return;
      }
      const d = snap.docs[0];
      setEntrepriseId(d.id);
      const data = d.data() || {};
      setShowStripeModal(!(data && data.base === 'active'));
    });
    return () => unsub();
  }, [user?.uid]);

  // ===============================
  // Dates auto à l’ajout d’un fichier
  // ===============================
  useEffect(() => {
    if (documentFile) {
      const now = new Date();
      setStartDateTime(formatDateTimeLocal(now));
      if (hasEndDate) {
        const oneWeekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        setEndDateTime(formatDateTimeLocal(oneWeekLater));
      } else {
        setEndDateTime("");
      }
    }
  }, [documentFile, hasEndDate]);

  // =================
  // Preview sélection
  // =================
  useEffect(() => {
    if (documentFile) {
      const url = URL.createObjectURL(documentFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [documentFile]);

  // ==========================
  // Charger logo + contenus (avec cache)
  // ==========================
  useEffect(() => {
    if (!user?.uid) return;
    const uid = user.uid;

    // 1) Logo: essaie le cache d'abord
    const cachedLogo = cacheGet(`profilePhotoUrl:${uid}`);
    if (cachedLogo) {
      setProfilePhotoUrl(cachedLogo);
    } else {
      // Fallback: on liste 1x puis on met en cache (TTL)
      const profileRef = ref(storage, `${uid}/photo-profil`);
      listAll(profileRef)
        .then(res => {
          if (res.items.length > 0) {
            return Promise.all(
              res.items.map(async (item) => {
                const metadata = await getMetadata(item);
                return { item, timeCreated: new Date(metadata.timeCreated) };
              })
            ).then(itemsWithMetadata => {
              itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
              const latestItem = itemsWithMetadata[0].item;
              return getDownloadURL(latestItem);
            });
          }
          return null;
        })
        .then((url) => {
          if (url) {
            setProfilePhotoUrl(url);
            cacheSet(`profilePhotoUrl:${uid}`, url);
          }
        })
        .catch(err => console.error("Erreur chargement photo profil:", err));
    }

    // 2) Pubs: essaie le cache d'abord
    const cachedPubs = cacheGet(`pubs:${uid}`);
    if (cachedPubs) {
      setPubs(
        cachedPubs.map(p => ({
          ...p,
          timeCreated: p.timeCreated ? new Date(p.timeCreated) : null,
          start: p.start ? new Date(p.start) : null,
          end: p.end ? new Date(p.end) : null,
        }))
      );
    } else {
      // Fallback: un fetch complet puis mise en cache
      const pubsRef = ref(storage, `${uid}/pub`);
      listAll(pubsRef)
        .then(async (res) => {
          const itemsWithMetadata = await Promise.all(
            res.items.map(async (item) => {
              const metadata = await getMetadata(item);
              const url = await getDownloadURL(item);
              const contentType = metadata.contentType || '';
              const kind = detectKind(contentType, item.name);
              const startMeta = metadata.customMetadata?.start;
              const endMeta = metadata.customMetadata?.end;

              return {
                url,
                name: item.name,
                contentType,
                kind,
                size: metadata.size,
                timeCreated: new Date(metadata.timeCreated),
                start: startMeta ? new Date(startMeta) : null,
                end: endMeta && endMeta !== "none" ? new Date(endMeta) : null,
              };
            })
          );
          itemsWithMetadata.sort((a, b) => b.timeCreated - a.timeCreated);
          setPubs(itemsWithMetadata);
          // on sérialise les dates
          cacheSet(`pubs:${uid}`, itemsWithMetadata.map(p => ({
            ...p,
            timeCreated: p.timeCreated ? p.timeCreated.toISOString() : null,
            start: p.start ? p.start.toISOString() : null,
            end: p.end ? p.end.toISOString() : null,
          })));
        })
        .catch(err => console.error("Erreur chargement contenus:", err));
    }
  }, [user?.uid]);

  // ==========================
  // Validation & Upload
  // ==========================
  const validateSelection = (file) => {
    if (!file) return { ok: false, msg: "Aucun fichier sélectionné." };

    if (!ALLOWED_MIME.includes(file.type)) {
      return { ok: false, msg: "Types autorisés : PNG, JPG, JPEG, PDF, MP4." };
    }

    const kind = detectKind(file.type, file.name);
    const max = kindMaxSize(kind);
    if (file.size > max) {
      const msg = kind === 'image'
        ? `Image trop lourde (max ${formatBytes(MAX_SIZE.image)}).`
        : kind === 'pdf'
          ? `PDF trop lourd (max ${formatBytes(MAX_SIZE.pdf)}).`
          : `Vidéo trop lourde (max ${formatBytes(MAX_SIZE.video)}).`;
      return { ok: false, msg };
    }

    return { ok: true, kind };
  };

  const handleFileUpload = async () => {
    if (!documentFile) { alert("Veuillez sélectionner un fichier."); return; }

    const check = validateSelection(documentFile);
    if (!check.ok) {
      setUploadStatus({ message: check.msg, type: "error" });
      setTimeout(() => setUploadStatus(null), 4000);
      return;
    }

    if (!startDateTime) { alert("Date/heure de début requise."); return; }
    if (hasEndDate && !endDateTime) { alert("Date/heure de fin requise."); return; }

    try {
      const currentUser = getAuth().currentUser;
      if (!currentUser) { alert("Utilisateur non authentifié."); return; }
      const uid = currentUser.uid;
      const fileExtension = documentFile.name.split('.').pop();
      const uniqueName = `${Date.now()}.${fileExtension}`;
      const fileRef = ref(storage, `${uid}/pub/${uniqueName}`);

      const metadata = {
        contentType: documentFile.type,
        customMetadata: {
          start: startDateTime,
          end: hasEndDate ? endDateTime : "none",
        }
      };

      await uploadBytes(fileRef, documentFile, metadata);

      // ✅ Pas de refresh complet : on ajoute juste l’élément uploadé en mémoire + cache
      const url = await getDownloadURL(fileRef);
      const kind = detectKind(documentFile.type, uniqueName);
      const newItem = {
        url,
        name: uniqueName,
        contentType: documentFile.type,
        kind,
        size: documentFile.size,
        timeCreated: new Date(), // approximatif: suffisant pour l’ordre local
        start: new Date(startDateTime),
        end: hasEndDate ? new Date(endDateTime) : null,
      };

      const newPubs = [newItem, ...pubs].sort((a, b) => (b.timeCreated?.getTime?.() || 0) - (a.timeCreated?.getTime?.() || 0));
      setPubs(newPubs);
      cacheSet(`pubs:${uid}`, newPubs.map(p => ({
        ...p,
        timeCreated: p.timeCreated ? p.timeCreated.toISOString() : null,
        start: p.start ? p.start.toISOString() : null,
        end: p.end ? p.end.toISOString() : null,
      })));

      // notifier les clients
      try {
        await updateDoc(doc(firestore, 'users', uid), { promoUpdatedAt: serverTimestamp() });
      } catch (e) {
        console.error('Impossible de mettre à jour promoUpdatedAt', e);
      }

      setUploadStatus({ message: "Fichier uploadé avec succès.", type: "success" });
      setDocumentFile(null);
      setStartDateTime("");
      setEndDateTime("");
      setHasEndDate(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setTimeout(() => setUploadStatus(null), 4000);
    } catch (error) {
      console.error("Erreur lors de l'upload:", error);
      setUploadStatus({ message: "Erreur lors de l'upload.", type: "error" });
      setTimeout(() => setUploadStatus(null), 4000);
    }
  };

  // ==========================
  // Filtres d'affichage
  // ==========================
  const now = useMemo(() => new Date(), []);
  const futurePubs = pubs.filter(pub => pub.start && pub.start > now);
  const currentPubs = pubs.filter(pub => pub.start && now >= pub.start && (!pub.end || now <= pub.end));

  // Ouvre le viewer avec l'URL du contenu + logo + pseudo
  const handleViewPub = (pubUrl) => {
    const params = new URLSearchParams({
      url: pubUrl,
      logo: profilePhotoUrl || '',
      pseudo: user?.pseudo || ''
    });
    navigate(`/merchantpubviewer?${params.toString()}`);
  };

  const handleDeletePub = async (pub) => {
    if (!user?.uid) return;
    const ok = window.confirm(`Supprimer “${sanitize(pub.name)}” ?`);
    if (!ok) return;
    try {
      setDeleting(pub.name);
      const fileRef = ref(storage, `${user.uid}/pub/${pub.name}`);
      await deleteObject(fileRef);

      setPubs((prev) => prev.filter((p) => p.name !== pub.name));
      cacheDel(`pubs:${user.uid}`);

      try {
        await updateDoc(doc(firestore, 'users', user.uid), { promoUpdatedAt: serverTimestamp() });
      } catch (e) {
        console.error('Impossible de mettre à jour promoUpdatedAt', e);
      }
    } catch (err) {
      console.error('Erreur suppression fichier:', err);
      alert("Échec de la suppression. Réessayez.");
    } finally {
      setDeleting(null);
    }
  };

  // ==========================
  // Rendu vignette selon type
  // ==========================
  const Thumb = React.memo(({ pub, small = false }) => {
    const baseStyle = {
      width: '100%',
      height: small ? '110px' : '150px',
      objectFit: 'cover',
      borderRadius: 'var(--eb-radius-sm)',
      display: 'block'
    };

    if (pub.kind === 'image') {
      return <img src={pub.url} alt={sanitize(pub.name)} style={baseStyle} loading="lazy" />;
    }
    if (pub.kind === 'video') {
      return (
        <video
          src={pub.url}
          style={baseStyle}
          controls={false}
          muted
          playsInline
          preload="metadata"
        />
      );
    }
    if (pub.kind === 'pdf') {
      return (
        <iframe
          src={pub.url}
          title={sanitize(pub.name)}
          loading="lazy"
          style={{
            width: '100%',
            height: small ? '110px' : '150px',
            border: 'none',
            borderRadius: 'var(--eb-radius-sm)'
          }}
        />
      );
    }

    return (
      <div style={{
        ...baseStyle,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--eb-surface-2)',
        border: '1px solid var(--eb-border)',
        color: 'var(--eb-muted)',
        fontSize: '13px'
      }}>
        Fichier
      </div>
    );
  });

  // ==========================
  // UI
  // ==========================
  return (
    <div>
      <MerchantNavbar />
      <div className="dashboard-container">
        <style>{`
          .mg-scope { width: 100%; min-width: 0; }
          .mg-scope .eb-page-title { margin: 0; text-align: left; }

          /* Onglets de la section Gestion (identiques à MerchantGestion) */
          .mg-scope .tabs-pill { display: inline-flex; gap: 4px; padding: 4px; margin: 0 0 24px; background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-xs); max-width: 100%; overflow-x: auto; scrollbar-width: none; }
          .mg-scope .tabs-pill::-webkit-scrollbar { display: none; }
          .mg-scope .tab { appearance: none; border: 0; background: transparent; color: var(--eb-text-2); font-weight: 600; font-size: 14px; min-height: 38px; padding: 0 16px; border-radius: var(--eb-radius-sm); cursor: pointer; white-space: nowrap; transition: background-color .15s ease, color .15s ease; }
          .mg-scope .tab:hover { background: var(--eb-surface-2); color: var(--eb-text); }
          .mg-scope .tab-active, .mg-scope .tab-active:hover { background: var(--eb-brand); color: #fff; cursor: default; box-shadow: var(--eb-shadow-xs); }

          .mg-scope .card, .mg-scope .section { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); padding: 24px; box-shadow: var(--eb-shadow-sm); }
          .mg-scope .section h2, .mg-scope .mp-title { margin: 0; font-size: 16px; font-weight: 650; color: var(--eb-text); text-align: left; }
          .mg-scope .mp-sub { margin: 4px 0 16px; font-size: 13.5px; color: var(--eb-muted); }

          /* Uploader */
          .mg-scope .uploader { display: flex; flex-direction: column; align-items: stretch; gap: 10px; }
          .mg-scope .mp-drop { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 24px 16px; border: 1.5px dashed var(--eb-border-strong); border-radius: var(--eb-radius); background: var(--eb-surface-2); text-align: center; }
          .mg-scope .uploader-hint { font-size: 13px; color: var(--eb-muted); text-align: center; line-height: 1.5; }
          .mg-scope .preview-frame { margin: 16px auto 8px; width: 100%; max-width: 260px; border: 1px solid var(--eb-border); border-radius: var(--eb-radius); overflow: hidden; background: var(--eb-surface); display: flex; align-items: center; justify-content: center; padding: 8px; }
          .mg-scope .mp-dates { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-top: 16px; }
          .mg-scope .input-ecb { width: 100%; min-height: 42px; padding: 0 12px; font-size: 15px; }
          .mg-scope .mp-actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--eb-border); }

          /* Grille de vignettes */
          .mg-scope .grid-cards { margin-top: 16px; display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 16px; }
          .mg-scope .tile { position: relative; border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 8px; cursor: pointer; background: var(--eb-surface); box-shadow: var(--eb-shadow-xs); transition: box-shadow .15s ease, border-color .15s ease; min-width: 0; }
          .mg-scope .tile:hover { border-color: var(--eb-border-strong); box-shadow: var(--eb-shadow-md); }
          .mg-scope .tile-title { margin-top: 8px; font-size: 13px; color: var(--eb-text-2); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .mg-scope .tile-actions { position: absolute; top: 14px; right: 14px; display: flex; gap: 6px; z-index: 1; }
          .mg-scope .tile-delete { width: 32px; height: 32px; border-radius: var(--eb-radius-pill); border: 1px solid var(--eb-border); background: var(--eb-surface); color: var(--eb-danger); font-size: 18px; font-weight: 600; line-height: 1; cursor: pointer; box-shadow: var(--eb-shadow-sm); display: inline-flex; align-items: center; justify-content: center; }
          .mg-scope .tile-delete:hover:not(:disabled) { background: var(--eb-danger-bg); border-color: #fbd0cb; }
          .mg-scope .tile-delete:disabled { opacity: 0.5; cursor: not-allowed; }

          .mg-scope .user-header { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
          .mg-scope .user-logo { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 1px solid var(--eb-border); }

          .mg-scope .status-msg { margin: 16px 0 0; }
          .mg-scope .eb-modal .status-msg { margin: 0; }
          .mg-scope .eb-modal .mp-dates { margin-top: 0; }
          .mg-scope .eb-modal .preview-frame { margin: 0 auto; }
          .mg-scope .eb-modal .mp-actions { margin-top: 0; }
          .mg-scope .mp-empty { margin: 0; padding: 32px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; border: 1px dashed var(--eb-border-strong); border-radius: var(--eb-radius-lg); background: var(--eb-surface); }

          @media (max-width: 768px) {
            .mg-scope .tabs-pill { display: flex; width: 100%; margin-bottom: 20px; }
            .mg-scope .tab { flex: 1 0 auto; padding: 0 12px; min-height: 40px; }
            .mg-scope .card, .mg-scope .section { padding: 18px 16px; }
            .mg-scope .grid-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
            .mg-scope .mp-actions { flex-direction: column-reverse; }
            .mg-scope .mp-actions > * { width: 100%; }
          }
        `}</style>

        <div className="mg-scope">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Gestion</h1>
            <p className="eb-page-subtitle">Publiez vos promotions auprès de vos clients</p>
          </div>
        </header>

        {/* Onglets */}
        <GestionTabs active="promo" />

        {showStripeModal ? (
          <div className="card">
            <h2 className="mp-title" style={{ fontSize: 18, marginBottom: 12 }}>Activez votre compte entreprise</h2>
            <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--eb-text-2)' }}>
              <li>
                <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px 12px' }}>
                  Activez votre compte :
                  <button
                    type="button"
                    className="button-classic"
                    onClick={() => {
                      const stripeUrl = process.env.NODE_ENV === 'production'
                        ? 'https://buy.stripe.com/fZeg04cUh6Z6b3W144'
                        : 'https://buy.stripe.com/test_00gaI28o69U1adidQQ';
                      window.open(stripeUrl, '_blank');
                    }}
                  >
                    Activer mon compte
                  </button>
                </span>
              </li>
              <li>Activation sous 24-48h après votre paiement</li>
              <li>Profitez pleinement de votre compte entreprise !</li>
            </ol>
          </div>
        ) : (
          <div>
            <div className="eb-toolbar">
              <h2 className="eb-toolbar__title">Promotions</h2>
              <div className="eb-toolbar__actions">
                <button type="button" className="eb-btn eb-btn--primary" onClick={() => setShowAddModal(true)}>
                  + Ajouter une promotion
                </button>
              </div>
            </div>

            {showAddModal && (
            <div
              className="eb-modal-overlay"
              onClick={(e) => { if (e.target === e.currentTarget) setShowAddModal(false); }}
            >
            <div className="eb-modal eb-modal--lg" role="dialog" aria-modal="true" aria-labelledby="mp-add-title">
            <div className="eb-modal__head">
              <h2 className="eb-modal__title" id="mp-add-title">Nouvelle promotion</h2>
              <button type="button" className="eb-modal__close" aria-label="Fermer" onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="eb-modal__body">
            <p className="mp-sub" style={{ margin: 0 }}>Image, PDF ou vidéo affiché à vos clients.</p>
            {/* Sélecteur de fichier */}
            <div className="uploader">
              <div className="mp-drop">
                <label htmlFor="fileInput" className="eb-btn eb-btn--secondary" style={{ cursor: 'pointer', whiteSpace: 'normal', textAlign: 'center', lineHeight: 1.3, padding: '10px 18px' }}>
                  {documentFile ? "Choisir un autre fichier" : "Importez un fichier (PNG/JPG/PDF/MP4)"}
                </label>
                <input
                  type="file"
                  id="fileInput"
                  ref={fileInputRef}
                  accept={ACCEPT_INPUT}
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      setDocumentFile(e.target.files[0]);
                    }
                  }}
                />
              <div className="uploader-hint">
                <div>Limites de poids : Images ≤ {formatBytes(MAX_SIZE.image)} · PDF ≤ {formatBytes(MAX_SIZE.pdf)} · MP4 ≤ {formatBytes(MAX_SIZE.video)}</div>
                {documentFile && (
                  <div style={{ marginTop: 6 }}>
                    Sélection : {sanitize(documentFile.name)} — {formatBytes(documentFile.size)}
                  </div>
                )}
              </div>
              </div>
            </div>

            {/* Preview de la sélection */}
            {documentFile && (
              <>
                <div className="preview-frame">
                  {previewUrl ? (
                    (() => {
                      const kind = detectKind(documentFile.type, documentFile.name);
                      if (kind === 'image') {
                        return (
                          <img
                            src={previewUrl}
                            alt="Aperçu"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '22vh',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'contain',
                              display: 'block'
                            }}
                          />
                        );
                      }
                      if (kind === 'video') {
                        return (
                          <video
                            src={previewUrl}
                            style={{ maxWidth: '100%', maxHeight: '22vh', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block' }}
                            controls
                            muted
                            playsInline
                            preload="metadata"
                          />
                        );
                      }
                      if (kind === 'pdf') {
                        return (
                          <iframe
                            src={`${previewUrl}#zoom=page-fit&scrollbar=0&toolbar=0&navpanes=0`}
                            title="Aperçu PDF"
                            style={{ width: '100%', height: '24vh', border: 'none' }}
                          />
                        );
                      }
                      return <p style={{ padding: 8, margin: 0, fontSize: 14, overflowWrap: 'anywhere' }}>{sanitize(documentFile.name)}</p>;
                    })()
                  ) : (
                    <p style={{ padding: 8, margin: 0, fontSize: 14, overflowWrap: 'anywhere' }}>{sanitize(documentFile.name)}</p>
                  )}
                </div>

                {/* Dates */}
                <div className="mp-dates">
                <div className="eb-field">
                  <label htmlFor="startDateTime" className="eb-label">Début</label>
                  <input
                    type="datetime-local"
                    id="startDateTime"
                    value={startDateTime}
                    onChange={(e) => setStartDateTime(sanitize(e.target.value))}
                    className="input-ecb"
                  />
                </div>

                {hasEndDate ? (
                  <div className="eb-field">
                    <label htmlFor="endDateTime" className="eb-label">Fin</label>
                    <input
                      type="datetime-local"
                      id="endDateTime"
                      value={endDateTime}
                      onChange={(e) => setEndDateTime(sanitize(e.target.value))}
                      className="input-ecb"
                    />
                    <button type="button" onClick={() => { setHasEndDate(false); setEndDateTime(""); }} className="eb-btn eb-btn--ghost eb-btn--sm" style={{ alignSelf: 'flex-start' }}>
                      Pas de date de fin
                    </button>
                  </div>
                ) : (
                  <div className="eb-field" style={{ justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setHasEndDate(true);
                        const now = new Date();
                        const oneWeekLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
                        setEndDateTime(formatDateTimeLocal(oneWeekLater));
                      }}
                      className="eb-btn eb-btn--secondary"
                      style={{ minHeight: 42 }}
                    >
                      Ajouter une date de fin
                    </button>
                  </div>
                )}
                </div>

                <div className="mp-actions">
                  <button type="button" onClick={handleFileUpload} className="eb-btn eb-btn--primary">
                    Publier
                  </button>
                </div>
              </>
            )}

            {uploadStatus && (
              <div role="status" className={`status-msg eb-alert ${uploadStatus.type === "success" ? "status-ok eb-alert--success" : "status-err eb-alert--danger"}`}>
                {uploadStatus.message}
              </div>
            )}
            </div>
            </div>
            </div>
            )}

            {/* Statut affiché aussi hors modale (ex. après fermeture) */}
            {!showAddModal && uploadStatus && (
              <div role="status" className={`status-msg eb-alert ${uploadStatus.type === "success" ? "status-ok eb-alert--success" : "status-err eb-alert--danger"}`} style={{ margin: '0 0 16px' }}>
                {uploadStatus.message}
              </div>
            )}

            {futurePubs.length === 0 && currentPubs.length === 0 && (
              <p className="mp-empty">Aucune promotion en cours ou à venir.</p>
            )}

            {/* À venir */}
            {futurePubs.length > 0 && (
              <div className="section">
                <h2>Contenus à venir</h2>
                <div className="grid-cards">
                  {futurePubs.map((pub, index) => (
                    <div
                      key={index}
                      className="tile"
                      onClick={() => handleViewPub(pub.url)}
                      title={`${sanitize(pub.name)}${pub.size ? ` — ${formatBytes(pub.size)}` : ''}`}
                    >
                      <div className="tile-actions">
                        <button
                          className="tile-delete"
                          aria-label="Supprimer"
                          disabled={deleting === pub.name}
                          onClick={(e) => { e.stopPropagation(); handleDeletePub(pub); }}
                        >
                          ×
                        </button>
                      </div>
                      <Thumb pub={pub} small />
                      <div className="tile-title">{sanitize(pub.name)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* En cours */}
            {currentPubs.length > 0 && (
              <div className="section" style={{ marginTop: futurePubs.length > 0 ? 20 : 0 }}>
                <div className="user-header">
                  {profilePhotoUrl && (
                    <img src={profilePhotoUrl} alt="Profil" className="user-logo" />
                  )}
                  <h2>
                    {sanitize(user?.pseudo || "Pseudo")}
                  </h2>
                </div>

                <div className="grid-cards">
                  {currentPubs.map((pub, index) => (
                    <div
                      key={index}
                      className="tile"
                      onClick={() => handleViewPub(pub.url)}
                      title={`${sanitize(pub.name)}${pub.size ? ` — ${formatBytes(pub.size)}` : ''}`}
                    >
                      <div className="tile-actions">
                        <button
                          className="tile-delete"
                          aria-label="Supprimer"
                          disabled={deleting === pub.name}
                          onClick={(e) => { e.stopPropagation(); handleDeletePub(pub); }}
                        >
                          ×
                        </button>
                      </div>
                      <Thumb pub={pub} />
                      <div className="tile-title">{sanitize(pub.name)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MerchantGestionPromo;
